/**
 * 🎟️ BETA INVITATIONS PAGE - Admin whitelist management for invite-only mode
 */

import { useState, useEffect } from 'react';
import apiClient from '@/services/apiClient';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  InputAdornment,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Email,
  Phone,
  Send,
  Delete,
  Refresh,
  CheckCircle,
  Schedule,
  Cancel as CancelIcon,
  Person
} from '@mui/icons-material';
import toast from 'react-hot-toast';

interface AdminInvitation {
  id: string;
  email?: string;
  phone?: string;
  status: 'pending' | 'accepted' | 'expired';
  message?: string;
  createdAt: string;
  acceptedAt?: string | null;
  expiresAt?: string;
  userId?: string | null;
  invitedBy: string | {
    id: string;
    email: string;
    fullName: string;
  };
  user?: {
    id: string;
    email: string;
    fullName: string;
  } | null;
}

export default function BetaInvitationsPage() {
  const [invitations, setInvitations] = useState<AdminInvitation[]>([]);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('You\'re invited to join PolarRide beta!');
  const [activeTab, setActiveTab] = useState<'email' | 'phone'>('email');
  const [bulkEmails, setBulkEmails] = useState('');
  const [defaultQuota, setDefaultQuota] = useState(5);
  const [bulkMode, setBulkMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  useEffect(() => {
    // Delay loading to prevent initial render errors
    const timer = setTimeout(() => {
      loadInvitations();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const loadInvitations = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/api/v1/admin/invitations/list');
      const data = response.data;

      if (data.success) {
        // Backend returns data.data.invitations or data.invitations
        const invitationsList = data.data?.invitations || data.invitations || [];

        // Ensure it's an array
        if (Array.isArray(invitationsList)) {
          setInvitations(invitationsList);
        } else {
          setInvitations([]);
        }
      } else {
        console.error('Failed to load invitations:', data);
        setInvitations([]);

        const errorMessage = typeof data.error === 'string'
          ? data.error
          : data.error?.message || data.message || 'Failed to load invitations';

        if (errorMessage && !errorMessage.toLowerCase().includes('not found')) {
          toast.error(errorMessage);
        }
      }
    } catch (error: any) {
      console.error('❌ Error loading invitations:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers
        }
      });
      setInvitations([]);

      // Handle specific error cases
      if (error.response?.status === 401) {
        const errorData = error.response?.data;
        const errorInfo = errorData?.error || errorData;
        const errorCode = errorInfo?.code || errorData?.code;
        const errorMessage = errorInfo?.message || errorData?.message || 'Authentication error';

        console.error('🚨 401 Error on invitations page:', {
          errorCode,
          errorMessage,
          fullError: errorData
        });

        // Show user-friendly error
        toast.error(`Authentication issue: ${errorMessage}. Try refreshing the page or logging out and back in.`);
      } else if (error.response?.data?.error?.code === 'MISSING_TOKEN') {
        toast.error('Session expired. Please log out and log back in.');
      } else if (error.response?.status === 500) {
        toast.error(`Backend error: ${error.response.data?.error?.message || 'Internal server error'}`);
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view invitations.');
      } else {
        toast.error('Failed to load invitations. Check console for details.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sendInvitation = async () => {
    // Handle bulk mode
    if (bulkMode && bulkEmails) {
      const emails = bulkEmails
        .split(/[\n,;]/)
        .map(e => e.trim())
        .filter(e => e && e.includes('@'));

      if (emails.length === 0) {
        toast.error('Please enter valid email addresses (one per line or comma-separated)');
        return;
      }

      try {
        const response = await apiClient.post('/api/v1/admin/invitations/send-bulk', {
          emails,
          message,
          quota: defaultQuota
        });

        if (response.data.success) {
          toast.success(`${emails.length} invitations sent successfully!`);
          setBulkEmails('');
          setSendDialogOpen(false);
          loadInvitations();
        } else {
          toast.error(response.data.error || 'Failed to send bulk invitations');
        }
      } catch (error: any) {
        console.error('Error sending bulk invitations:', error);
        if (error.response?.data?.error?.code === 'MISSING_TOKEN') {
          toast.error('Session expired. Please log out and log back in.');
          setSendDialogOpen(false);
        } else {
          toast.error(error.response?.data?.error?.message || 'Failed to send bulk invitations');
        }
      }
      return;
    }

    // Single invitation mode
    const recipient = activeTab === 'email' ? email : phone;

    if (!recipient.trim()) {
      toast.error(`Please enter a valid ${activeTab}`);
      return;
    }

    try {
      const response = await apiClient.post('/api/v1/admin/invitations/send', {
        email: activeTab === 'email' ? email : undefined,
        phone: activeTab === 'phone' ? phone : undefined,
        message,
        quota: defaultQuota
      });

      if (response.data.success) {
        toast.success(`Invitation sent to ${recipient}`);
        setEmail('');
        setPhone('');
        setSendDialogOpen(false);
        loadInvitations();
      } else {
        toast.error(response.data.error || 'Failed to send invitation');
      }
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      if (error.response?.data?.error?.code === 'MISSING_TOKEN') {
        toast.error('Session expired. Please log out and log back in.');
        setSendDialogOpen(false);
      } else {
        toast.error(error.response?.data?.error?.message || 'Failed to send invitation. Check console for details.');
      }
    }
  };

  const cancelInvitation = async (invitationId: string) => {
    try {

      const response = await apiClient.delete(`/api/v1/admin/invitations/${invitationId}`);

      if (response.data.success) {
        toast.success('Invitation canceled');
        loadInvitations();
      } else {
        toast.error(response.data.error || 'Failed to cancel invitation');
      }
    } catch (error: any) {
      console.error('Error canceling invitation:', error);
      if (error.response?.data?.error?.code === 'MISSING_TOKEN') {
        toast.error('Session expired. Please log out and log back in.');
      } else {
        toast.error(error.response?.data?.error?.message || 'Failed to cancel invitation');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'pending': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Beta Invitations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Whitelist users for invite-only mode registration
        </Typography>
      </Box>

      {/* Send Invitation Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Send Invitation
            </Typography>
            <Button
              variant="contained"
              startIcon={<Send />}
              onClick={() => setSendDialogOpen(true)}
            >
              Send Invitation
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Users who receive invitations will be whitelisted and can register even when invite-only mode is enabled.
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                OR
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+234..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Invitations List */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Sent Invitations
            </Typography>
            <IconButton onClick={loadInvitations}>
              <Refresh />
            </IconButton>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sent Date</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Invited By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invitations.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person fontSize="small" />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {inv.email || inv.phone}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {inv.email ? 'Email' : 'Phone'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={inv.status}
                        size="small"
                        color={getStatusColor(inv.status) as any}
                        icon={
                          inv.status === 'accepted' ? <CheckCircle /> :
                          inv.status === 'pending' ? <Schedule /> :
                          <CancelIcon />
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(inv.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {inv.expiresAt ? (
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(inv.expiresAt)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No expiry
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {typeof inv.invitedBy === 'string' ? inv.invitedBy : (inv.invitedBy?.fullName || inv.invitedBy?.email || 'Admin')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {typeof inv.invitedBy === 'string' ? 'Admin' : (inv.invitedBy?.email || '')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={inv.status === 'accepted' ? 'Delete invitation and user account' : 'Delete invitation'}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => cancelInvitation(inv.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}

                {invitations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Paper sx={{ p: 6, bgcolor: 'action.hover' }}>
                        <Email sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          No invitations sent yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Send your first invitation to start building your beta user base
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Send Invitation Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send Beta Invitation</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Invited users will be whitelisted and can register even when invite-only mode is enabled.
              They will receive {defaultQuota === -1 ? 'unlimited' : defaultQuota} invite{defaultQuota !== 1 ? 's' : ''} to share.
            </Alert>

            {/* Bulk Mode Toggle */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2">Single Invitation</Typography>
              <Switch
                checked={bulkMode}
                onChange={(e) => setBulkMode(e.target.checked)}
              />
              <Typography variant="body2" sx={{ fontWeight: bulkMode ? 600 : 400 }}>
                Bulk Invitations
              </Typography>
            </Box>

            {!bulkMode ? (
              <>
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => setActiveTab(newValue)}
                  sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab label="Email" value="email" icon={<Email />} iconPosition="start" />
                  <Tab label="Phone" value="phone" icon={<Phone />} iconPosition="start" />
                </Tabs>

                {activeTab === 'email' ? (
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="user@example.com"
                    autoFocus
                    sx={{ mb: 2 }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+234..."
                    autoFocus
                    sx={{ mb: 2 }}
                  />
                )}
              </>
            ) : (
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Email Addresses (Bulk)"
                value={bulkEmails}
                onChange={(e) => setBulkEmails(e.target.value)}
                placeholder="Enter email addresses (one per line, or comma-separated)&#10;user1@example.com&#10;user2@example.com&#10;user3@example.com"
                sx={{ mb: 2 }}
                helperText={`${bulkEmails.split(/[\n,;]/).filter(e => e.trim() && e.includes('@')).length} valid email(s) detected`}
              />
            )}

            <TextField
              fullWidth
              type="number"
              label="Default Invite Quota"
              value={defaultQuota}
              onChange={(e) => setDefaultQuota(parseInt(e.target.value) || 5)}
              helperText="How many people can each invited user invite? Enter -1 for unlimited"
              inputProps={{ min: -1, step: 1 }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Invitation Message (Customize)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="You're invited to join PolarRide beta!"
              helperText="This message will be included in the email/SMS sent to invited users"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={sendInvitation}
            startIcon={<Send />}
          >
            {bulkMode ? `Send ${bulkEmails.split(/[\n,;]/).filter(e => e.trim() && e.includes('@')).length} Invitations` : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
